import React, { useState, useEffect, useMemo } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  onSnapshot, 
  addDoc, 
  updateDoc, 
  doc, 
  serverTimestamp, 
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../App';
import { 
  Truck, 
  Plus, 
  Car, 
  Fuel, 
  MapPin, 
  Calendar, 
  Clock, 
  Wrench, 
  CheckCircle2, 
  XCircle, 
  TrendingUp, 
  User as UserIcon,
  Search,
  Filter
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface Vehicle {
  id?: string;
  plateNumber: string;
  makeModel: string;
  driverName: string;
  status: 'active' | 'idle' | 'maintenance';
  odometer: number;
  lastServiceDate: string;
}

interface Trip {
  id?: string;
  date: string;
  vehicleId: string;
  vehiclePlate: string;
  driverName: string;
  destination: string;
  purpose: string;
  startKm: number;
  endKm?: number;
  status: 'in_progress' | 'completed';
  createdAt?: any;
}

interface FuelVoucher {
  id?: string;
  date: string;
  vehiclePlate: string;
  amount: number;
  liters: number;
  station: string;
  requestedBy: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt?: any;
}

export default function TransportationDashboard() {
  const { profile, isAdmin, isManager } = useAuth();
  const [activeTab, setActiveTab] = useState<'fleet' | 'trips' | 'vouchers'>('fleet');
  
  // Real-time Firestore States
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [vouchers, setVouchers] = useState<FuelVoucher[]>([]);

  // Search & Filter
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal control
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [isTripModalOpen, setIsTripModalOpen] = useState(false);
  const [isVoucherModalOpen, setIsVoucherModalOpen] = useState(false);

  // Form States
  const [vehicleForm, setVehicleForm] = useState({
    plateNumber: '',
    makeModel: '',
    driverName: '',
    status: 'idle' as 'active' | 'idle' | 'maintenance',
    odometer: 0,
    lastServiceDate: format(new Date(), 'yyyy-MM-dd')
  });

  const [tripForm, setTripForm] = useState({
    vehicleId: '',
    destination: '',
    purpose: '',
    startKm: 0
  });

  const [voucherForm, setVoucherForm] = useState({
    vehiclePlate: '',
    amount: 0,
    liters: 0,
    station: ''
  });

  // Subscribe to collections
  useEffect(() => {
    const unsubVehicles = onSnapshot(
      query(collection(db, 'logistics_vehicles'), orderBy('plateNumber', 'asc')),
      (snapshot) => {
        const loaded = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Vehicle));
        setVehicles(loaded);
        
        // Seed default vehicles if collection is empty
        if (loaded.length === 0) {
          const defaults: Vehicle[] = [
            { plateNumber: 'T 123 ABC', makeModel: 'Toyota Hilux 2021', driverName: 'Hamisi Juma', status: 'idle', odometer: 45200, lastServiceDate: '2026-05-15' },
            { plateNumber: 'T 456 DEF', makeModel: 'Mitsubishi L200', driverName: 'John Mwangi', status: 'active', odometer: 89300, lastServiceDate: '2026-04-10' },
            { plateNumber: 'T 789 GHI', makeModel: 'Toyota Land Cruiser', driverName: 'Peter Massawe', status: 'maintenance', odometer: 125000, lastServiceDate: '2026-06-01' }
          ];
          defaults.forEach(v => {
            addDoc(collection(db, 'logistics_vehicles'), v);
          });
        }
      }
    );

    const unsubTrips = onSnapshot(
      query(collection(db, 'logistics_trips'), orderBy('date', 'desc')),
      (snapshot) => {
        setTrips(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Trip)));
      }
    );

    const unsubVouchers = onSnapshot(
      query(collection(db, 'logistics_fuel_vouchers'), orderBy('date', 'desc')),
      (snapshot) => {
        setVouchers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FuelVoucher)));
      }
    );

    return () => {
      unsubVehicles();
      unsubTrips();
      unsubVouchers();
    };
  }, []);

  // Handlers
  const handleAddVehicle = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!vehicleForm.plateNumber || !vehicleForm.makeModel) {
      toast.error('Please fill in all required fields');
      return;
    }
    try {
      await addDoc(collection(db, 'logistics_vehicles'), {
        ...vehicleForm,
        odometer: Number(vehicleForm.odometer)
      });
      toast.success('Vehicle registered successfully');
      setIsVehicleModalOpen(false);
      setVehicleForm({
        plateNumber: '',
        makeModel: '',
        driverName: '',
        status: 'idle',
        odometer: 0,
        lastServiceDate: format(new Date(), 'yyyy-MM-dd')
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to register vehicle');
    }
  };

  const handleStartTrip = async (e: React.FormEvent) => {
    e.preventDefault();
    const selectedVeh = vehicles.find(v => v.id === tripForm.vehicleId);
    if (!selectedVeh) {
      toast.error('Please select a vehicle');
      return;
    }
    try {
      // 1. Add Trip Log
      await addDoc(collection(db, 'logistics_trips'), {
        date: format(new Date(), 'yyyy-MM-dd'),
        vehicleId: tripForm.vehicleId,
        vehiclePlate: selectedVeh.plateNumber,
        driverName: selectedVeh.driverName,
        destination: tripForm.destination,
        purpose: tripForm.purpose,
        startKm: Number(tripForm.startKm || selectedVeh.odometer),
        status: 'in_progress',
        createdAt: serverTimestamp()
      });

      // 2. Update Vehicle status to 'active'
      await updateDoc(doc(db, 'logistics_vehicles', selectedVeh.id!), {
        status: 'active'
      });

      toast.success('Trip started successfully');
      setIsTripModalOpen(false);
      setTripForm({
        vehicleId: '',
        destination: '',
        purpose: '',
        startKm: 0
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to start trip');
    }
  };

  const handleCompleteTrip = async (tripId: string, endKm: number) => {
    const trip = trips.find(t => t.id === tripId);
    if (!trip) return;
    if (endKm <= trip.startKm) {
      toast.error('End KM must be greater than Start KM');
      return;
    }
    try {
      await updateDoc(doc(db, 'logistics_trips', tripId), {
        endKm: Number(endKm),
        status: 'completed'
      });

      const vehDocRef = doc(db, 'logistics_vehicles', trip.vehicleId);
      await updateDoc(vehDocRef, {
        status: 'idle',
        odometer: Number(endKm)
      });

      toast.success('Trip completed successfully');
    } catch (err) {
      console.error(err);
      toast.error('Failed to complete trip');
    }
  };

  const handleCreateVoucher = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!voucherForm.vehiclePlate || !voucherForm.amount) {
      toast.error('Please enter vehicle and voucher amount');
      return;
    }
    try {
      await addDoc(collection(db, 'logistics_fuel_vouchers'), {
        date: format(new Date(), 'yyyy-MM-dd'),
        vehiclePlate: voucherForm.vehiclePlate,
        amount: Number(voucherForm.amount),
        liters: Number(voucherForm.liters),
        station: voucherForm.station,
        requestedBy: profile?.displayName || profile?.email || 'Logistics Officer',
        status: 'pending',
        createdAt: serverTimestamp()
      });
      toast.success('Fuel voucher request submitted');
      setIsVoucherModalOpen(false);
      setVoucherForm({
        vehiclePlate: '',
        amount: 0,
        liters: 0,
        station: ''
      });
    } catch (err) {
      console.error(err);
      toast.error('Failed to submit fuel voucher');
    }
  };

  const handleUpdateVoucherStatus = async (id: string, status: 'approved' | 'rejected') => {
    try {
      await updateDoc(doc(db, 'logistics_fuel_vouchers', id), { status });
      toast.success(`Voucher request ${status}`);
    } catch (err) {
      console.error(err);
      toast.error('Failed to update voucher status');
    }
  };

  // Memoized lists
  const filteredVehicles = useMemo(() => {
    return vehicles.filter(v => {
      const matchSearch = v.plateNumber.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          v.makeModel.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          v.driverName.toLowerCase().includes(searchQuery.toLowerCase());
      const matchStatus = statusFilter === 'all' ? true : v.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [vehicles, searchQuery, statusFilter]);

  // Key Metrics
  const metrics = useMemo(() => {
    const total = vehicles.length;
    const active = vehicles.filter(v => v.status === 'active').length;
    const maintenance = vehicles.filter(v => v.status === 'maintenance').length;
    const pendingVouchers = vouchers.filter(v => v.status === 'pending').length;
    return { total, active, maintenance, pendingVouchers };
  }, [vehicles, vouchers]);

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8 bg-slate-50 min-h-screen">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
        <div>
          <span className="px-3 py-1 bg-blue-50 text-blue-800 rounded-full text-xs font-black uppercase tracking-widest">
            Logistics & Fleet Operations
          </span>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 mt-2">Transportation Dashboard</h1>
          <p className="text-slate-500 font-medium">Manage fleet dispatch, log trips, and request fuel vouchers.</p>
        </div>
        
        <div className="flex gap-3">
          {activeTab === 'fleet' && (
            <button 
              onClick={() => setIsVehicleModalOpen(true)}
              className="px-6 py-3.5 bg-blue-900 text-white rounded-2xl font-black text-sm uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-blue-900/20 active:scale-95 transition-all"
            >
              <Plus className="w-5 h-5" />
              Register Vehicle
            </button>
          )}
          {activeTab === 'trips' && (
            <button 
              onClick={() => setIsTripModalOpen(true)}
              className="px-6 py-3.5 bg-emerald-600 text-white rounded-2xl font-black text-sm uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-emerald-600/20 active:scale-95 transition-all"
            >
              <Plus className="w-5 h-5" />
              Log/Dispatch Trip
            </button>
          )}
          {activeTab === 'vouchers' && (
            <button 
              onClick={() => setIsVoucherModalOpen(true)}
              className="px-6 py-3.5 bg-indigo-600 text-white rounded-2xl font-black text-sm uppercase tracking-wider flex items-center gap-2 shadow-lg shadow-indigo-600/20 active:scale-95 transition-all"
            >
              <Plus className="w-5 h-5" />
              Request Voucher
            </button>
          )}
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-5">
          <div className="p-4 bg-blue-50 text-blue-900 rounded-2xl">
            <Car className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-black text-slate-400 uppercase tracking-widest block">Total Fleet</span>
            <span className="text-2xl font-black text-slate-800">{metrics.total} Vehicles</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-5">
          <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl">
            <Truck className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-black text-slate-400 uppercase tracking-widest block">Active Trips</span>
            <span className="text-2xl font-black text-slate-800">{metrics.active} On Road</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-5">
          <div className="p-4 bg-amber-50 text-amber-600 rounded-2xl">
            <Wrench className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-black text-slate-400 uppercase tracking-widest block">Under Maintenance</span>
            <span className="text-2xl font-black text-slate-800">{metrics.maintenance} In Garage</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-3xl shadow-sm border border-slate-100 flex items-center gap-5">
          <div className="p-4 bg-indigo-50 text-indigo-600 rounded-2xl">
            <Fuel className="w-6 h-6" />
          </div>
          <div>
            <span className="text-xs font-black text-slate-400 uppercase tracking-widest block">Pending Vouchers</span>
            <span className="text-2xl font-black text-slate-800">{metrics.pendingVouchers} Requests</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200 gap-2">
        <button 
          onClick={() => setActiveTab('fleet')}
          className={`px-6 py-3.5 font-bold text-sm uppercase tracking-wider border-b-4 transition-all ${activeTab === 'fleet' ? 'border-blue-950 text-blue-950' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
        >
          Fleet Directory
        </button>
        <button 
          onClick={() => setActiveTab('trips')}
          className={`px-6 py-3.5 font-bold text-sm uppercase tracking-wider border-b-4 transition-all relative ${activeTab === 'trips' ? 'border-blue-950 text-blue-950' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
        >
          Trip Logbooks
          {trips.filter(t => t.status === 'in_progress').length > 0 && (
            <span className="ml-2 bg-emerald-500 text-white rounded-full text-[10px] font-bold px-2 py-0.5">
              {trips.filter(t => t.status === 'in_progress').length} active
            </span>
          )}
        </button>
        <button 
          onClick={() => setActiveTab('vouchers')}
          className={`px-6 py-3.5 font-bold text-sm uppercase tracking-wider border-b-4 transition-all relative ${activeTab === 'vouchers' ? 'border-blue-950 text-blue-950' : 'border-transparent text-slate-400 hover:text-slate-600'}`}
        >
          Fuel Vouchers
          {metrics.pendingVouchers > 0 && (
            <span className="ml-2 bg-indigo-500 text-white rounded-full text-[10px] font-bold px-2 py-0.5">
              {metrics.pendingVouchers}
            </span>
          )}
        </button>
      </div>

      {/* Main Content Sections */}
      {activeTab === 'fleet' && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          
          {/* Controls */}
          <div className="p-6 bg-slate-50/50 border-b border-slate-100 flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="relative w-full sm:max-w-md">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input 
                type="text"
                placeholder="Search plate, model, driver..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-5 py-3 bg-white border border-slate-200 rounded-2xl font-semibold text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-900/10 transition-all text-sm"
              />
            </div>

            <div className="flex gap-2 w-full sm:w-auto">
              <select 
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-5 py-3 bg-white border border-slate-200 rounded-2xl font-bold text-slate-700 text-sm focus:outline-none"
              >
                <option value="all">All Statuses</option>
                <option value="idle">Idle / Ready</option>
                <option value="active">Active On Trip</option>
                <option value="maintenance">In Maintenance</option>
              </select>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 uppercase tracking-wider text-[11px] font-black">
                  <th className="py-4 px-6">Vehicle Details</th>
                  <th className="py-4 px-6">Assigned Driver</th>
                  <th className="py-4 px-6">Odometer</th>
                  <th className="py-4 px-6">Last Serviced</th>
                  <th className="py-4 px-6">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {filteredVehicles.map(veh => (
                  <tr key={veh.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="font-bold text-slate-800">{veh.plateNumber}</div>
                      <div className="text-slate-400 text-xs font-semibold">{veh.makeModel}</div>
                    </td>
                    <td className="py-4 px-6 font-semibold text-slate-700">{veh.driverName}</td>
                    <td className="py-4 px-6 font-bold text-slate-700">{veh.odometer.toLocaleString()} KM</td>
                    <td className="py-4 px-6 text-slate-500 font-medium">{veh.lastServiceDate || 'N/A'}</td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-bold inline-flex items-center gap-1.5 uppercase tracking-wider ${
                        veh.status === 'active' ? 'bg-emerald-50 text-emerald-800' :
                        veh.status === 'maintenance' ? 'bg-amber-50 text-amber-800' :
                        'bg-slate-100 text-slate-600'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          veh.status === 'active' ? 'bg-emerald-500' :
                          veh.status === 'maintenance' ? 'bg-amber-500' :
                          'bg-slate-400'
                        }`} />
                        {veh.status}
                      </span>
                    </td>
                  </tr>
                ))}
                {filteredVehicles.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-12 text-center text-slate-400 italic">No vehicles matched your search query.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'trips' && (
        <div className="space-y-6">
          
          {/* Active Trips section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {trips.filter(t => t.status === 'in_progress').map(trip => {
              // Create local handler state for trip end Km
              return (
                <div key={trip.id} className="bg-white p-6 rounded-3xl shadow-sm border border-emerald-100 bg-emerald-50/10 flex flex-col justify-between gap-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="px-2.5 py-1 bg-emerald-100 text-emerald-800 rounded-full text-[10px] font-black uppercase tracking-wider">
                        In Progress
                      </span>
                      <h3 className="text-lg font-black text-slate-800 mt-2">{trip.vehiclePlate} — {trip.destination}</h3>
                      <p className="text-slate-400 text-xs font-semibold">{trip.purpose}</p>
                    </div>
                    <div className="text-right text-xs font-semibold text-slate-500">
                      <div>Driver: <span className="font-bold text-slate-700">{trip.driverName}</span></div>
                      <div>Start KM: <span className="font-bold text-slate-700">{trip.startKm}</span></div>
                    </div>
                  </div>

                  <form 
                    onSubmit={(e) => {
                      e.preventDefault();
                      const val = (e.target as any).endKm.value;
                      if (!val) {
                        toast.error('Please input End Odometer reading');
                        return;
                      }
                      handleCompleteTrip(trip.id!, Number(val));
                    }}
                    className="flex gap-2 items-center border-t border-slate-100 pt-4"
                  >
                    <input 
                      type="number"
                      name="endKm"
                      placeholder="End Odometer KM"
                      required
                      className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-xl font-bold text-slate-900 focus:outline-none text-xs"
                    />
                    <button 
                      type="submit"
                      className="px-5 py-3 bg-emerald-600 text-white rounded-xl text-xs font-bold uppercase tracking-wider hover:bg-emerald-700"
                    >
                      Complete Trip
                    </button>
                  </form>
                </div>
              );
            })}
            {trips.filter(t => t.status === 'in_progress').length === 0 && (
              <div className="col-span-full bg-white py-8 px-6 rounded-3xl border border-slate-100 text-center italic text-slate-400">
                No trips are currently active/in-progress.
              </div>
            )}
          </div>

          {/* History */}
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="p-6 border-b border-slate-100">
              <h2 className="text-lg font-black text-slate-800">Completed Trip Logs History</h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 uppercase tracking-wider text-[11px] font-black">
                    <th className="py-4 px-6">Date</th>
                    <th className="py-4 px-6">Vehicle</th>
                    <th className="py-4 px-6">Driver Name</th>
                    <th className="py-4 px-6">Route details</th>
                    <th className="py-4 px-6">Start KM</th>
                    <th className="py-4 px-6">End KM</th>
                    <th className="py-4 px-6">Total Distance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-sm">
                  {trips.filter(t => t.status === 'completed').map(trip => (
                    <tr key={trip.id} className="hover:bg-slate-50/50">
                      <td className="py-4 px-6 font-bold text-slate-600">{trip.date}</td>
                      <td className="py-4 px-6 font-bold text-blue-900">{trip.vehiclePlate}</td>
                      <td className="py-4 px-6 font-semibold text-slate-700">{trip.driverName}</td>
                      <td className="py-4 px-6">
                        <div className="font-semibold text-slate-800">To {trip.destination}</div>
                        <div className="text-slate-400 text-xs font-semibold">{trip.purpose}</div>
                      </td>
                      <td className="py-4 px-6 font-semibold text-slate-600">{trip.startKm} KM</td>
                      <td className="py-4 px-6 font-semibold text-slate-600">{trip.endKm} KM</td>
                      <td className="py-4 px-6 font-bold text-slate-800">{(trip.endKm || 0) - trip.startKm} KM</td>
                    </tr>
                  ))}
                  {trips.filter(t => t.status === 'completed').length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-slate-400 italic">No completed trips found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {activeTab === 'vouchers' && (
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex justify-between items-center">
            <h2 className="text-lg font-black text-slate-800">Fuel Voucher Transactions Log</h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100 text-slate-400 uppercase tracking-wider text-[11px] font-black">
                  <th className="py-4 px-6">Request Date</th>
                  <th className="py-4 px-6">Vehicle</th>
                  <th className="py-4 px-6">Requested By</th>
                  <th className="py-4 px-6">Fuel Station</th>
                  <th className="py-4 px-6">Details (Liters)</th>
                  <th className="py-4 px-6">Amount (TZS)</th>
                  <th className="py-4 px-6">Approval Status</th>
                  {(isAdmin || isManager) && <th className="py-4 px-6 text-center">Admin Controls</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {vouchers.map(v => (
                  <tr key={v.id} className="hover:bg-slate-50/50">
                    <td className="py-4 px-6 font-bold text-slate-600">{v.date}</td>
                    <td className="py-4 px-6 font-bold text-blue-950">{v.vehiclePlate}</td>
                    <td className="py-4 px-6 font-semibold text-slate-700">{v.requestedBy}</td>
                    <td className="py-4 px-6 font-semibold text-slate-600">{v.station || 'N/A'}</td>
                    <td className="py-4 px-6 text-slate-500 font-bold">{v.liters ? `${v.liters} L` : 'N/A'}</td>
                    <td className="py-4 px-6 font-black text-slate-800">TZS {v.amount.toLocaleString()}</td>
                    <td className="py-4 px-6">
                      <span className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 ${
                        v.status === 'approved' ? 'bg-emerald-50 text-emerald-800' :
                        v.status === 'rejected' ? 'bg-red-50 text-red-800' :
                        'bg-indigo-50 text-indigo-800'
                      }`}>
                        {v.status === 'approved' && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                        {v.status === 'rejected' && <XCircle className="w-3.5 h-3.5 text-red-500" />}
                        {v.status === 'pending' && <Clock className="w-3.5 h-3.5 text-indigo-500" />}
                        {v.status}
                      </span>
                    </td>
                    {(isAdmin || isManager) && (
                      <td className="py-4 px-6">
                        {v.status === 'pending' ? (
                          <div className="flex gap-1.5 justify-center">
                            <button 
                              onClick={() => handleUpdateVoucherStatus(v.id!, 'approved')}
                              className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold hover:bg-emerald-700"
                            >
                              Approve
                            </button>
                            <button 
                              onClick={() => handleUpdateVoucherStatus(v.id!, 'rejected')}
                              className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-bold hover:bg-red-700"
                            >
                              Reject
                            </button>
                          </div>
                        ) : (
                          <div className="text-xs text-slate-400 text-center font-semibold italic">Processed</div>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {vouchers.length === 0 && (
                  <tr>
                    <td colSpan={(isAdmin || isManager) ? 8 : 7} className="py-12 text-center text-slate-400 italic">No fuel voucher records found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* MODAL 1: Register Vehicle */}
      {isVehicleModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-blue-950/40 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] w-full max-w-lg p-8 shadow-2xl relative">
            <h2 className="text-xl font-black text-slate-800 mb-6">Register New Vehicle</h2>
            <form onSubmit={handleAddVehicle} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Plate Number</label>
                <input 
                  type="text" 
                  placeholder="e.g. T 123 ABC"
                  required
                  value={vehicleForm.plateNumber}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, plateNumber: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Make / Model</label>
                <input 
                  type="text" 
                  placeholder="e.g. Mitsubishi L200"
                  required
                  value={vehicleForm.makeModel}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, makeModel: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Assigned Driver</label>
                <input 
                  type="text" 
                  placeholder="Driver's Full Name"
                  required
                  value={vehicleForm.driverName}
                  onChange={(e) => setVehicleForm({ ...vehicleForm, driverName: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Odometer Reading (KM)</label>
                  <input 
                    type="number" 
                    placeholder="Current KM"
                    value={vehicleForm.odometer}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, odometer: Number(e.target.value) })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Last Service Date</label>
                  <input 
                    type="date" 
                    value={vehicleForm.lastServiceDate}
                    onChange={(e) => setVehicleForm({ ...vehicleForm, lastServiceDate: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                  />
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button 
                  type="button" 
                  onClick={() => setIsVehicleModalOpen(false)}
                  className="px-5 py-3 bg-slate-100 text-slate-500 rounded-xl font-bold text-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-blue-900 text-white rounded-xl font-bold text-sm"
                >
                  Register
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: Log / Dispatch Trip */}
      {isTripModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-blue-950/40 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] w-full max-w-lg p-8 shadow-2xl relative">
            <h2 className="text-xl font-black text-slate-800 mb-6">Log & Dispatch Trip</h2>
            <form onSubmit={handleStartTrip} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Select Dispatch Vehicle</label>
                <select 
                  required
                  value={tripForm.vehicleId}
                  onChange={(e) => {
                    const matched = vehicles.find(v => v.id === e.target.value);
                    setTripForm({ 
                      ...tripForm, 
                      vehicleId: e.target.value,
                      startKm: matched ? matched.odometer : 0
                    });
                  }}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                >
                  <option value="">-- Choose Vehicle --</option>
                  {vehicles.filter(v => v.status === 'idle').map(v => (
                    <option key={v.id} value={v.id}>
                      {v.plateNumber} ({v.makeModel}) - Drv: {v.driverName}
                    </option>
                  ))}
                </select>
                {vehicles.filter(v => v.status === 'idle').length === 0 && (
                  <p className="text-[11px] text-amber-600 font-bold mt-1">
                    ⚠️ No idle vehicles available. Please complete ongoing trips or update vehicle status.
                  </p>
                )}
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Destination Location</label>
                <input 
                  type="text" 
                  placeholder="e.g. Kigamboni Sub-station"
                  required
                  value={tripForm.destination}
                  onChange={(e) => setTripForm({ ...tripForm, destination: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Purpose of Trip</label>
                <textarea 
                  placeholder="e.g. Delivery of spare parts for AC main compressor overhauling"
                  required
                  rows={2}
                  value={tripForm.purpose}
                  onChange={(e) => setTripForm({ ...tripForm, purpose: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none text-sm"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Departure Odometer KM (Leave as is to use current odometer)</label>
                <input 
                  type="number" 
                  value={tripForm.startKm}
                  onChange={(e) => setTripForm({ ...tripForm, startKm: Number(e.target.value) })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button 
                  type="button" 
                  onClick={() => setIsTripModalOpen(false)}
                  className="px-5 py-3 bg-slate-100 text-slate-500 rounded-xl font-bold text-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-emerald-600 text-white rounded-xl font-bold text-sm uppercase tracking-wider"
                >
                  Dispatch
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: Request Fuel Voucher */}
      {isVoucherModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-blue-950/40 backdrop-blur-sm">
          <div className="bg-white rounded-[32px] w-full max-w-lg p-8 shadow-2xl relative">
            <h2 className="text-xl font-black text-slate-800 mb-6">Request Fuel Voucher</h2>
            <form onSubmit={handleCreateVoucher} className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Vehicle Plate Number</label>
                <select 
                  required
                  value={voucherForm.vehiclePlate}
                  onChange={(e) => setVoucherForm({ ...voucherForm, vehiclePlate: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                >
                  <option value="">-- Choose Vehicle --</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.plateNumber}>
                      {v.plateNumber} ({v.makeModel})
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Voucher Amount (TZS)</label>
                  <input 
                    type="number" 
                    placeholder="e.g. 50000"
                    required
                    value={voucherForm.amount || ''}
                    onChange={(e) => setVoucherForm({ ...voucherForm, amount: Number(e.target.value) })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Fuel Quantity (Liters)</label>
                  <input 
                    type="number" 
                    placeholder="Liters"
                    value={voucherForm.liters || ''}
                    onChange={(e) => setVoucherForm({ ...voucherForm, liters: Number(e.target.value) })}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-black uppercase tracking-wider text-slate-400 ml-1">Fuel Station</label>
                <input 
                  type="text" 
                  placeholder="e.g. Puma Energy (Kigamboni)"
                  required
                  value={voucherForm.station}
                  onChange={(e) => setVoucherForm({ ...voucherForm, station: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-xl font-bold focus:outline-none"
                />
              </div>

              <div className="flex gap-3 justify-end pt-4">
                <button 
                  type="button" 
                  onClick={() => setIsVoucherModalOpen(false)}
                  className="px-5 py-3 bg-slate-100 text-slate-500 rounded-xl font-bold text-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-indigo-600 text-white rounded-xl font-bold text-sm uppercase tracking-wider"
                >
                  Request
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
